import { AxiosInstance } from 'axios';
import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { uniq } from 'lodash';
import { createViewModel } from 'mobx-utils/lib/create-view-model';
import GlobalStore from './GlobalStore';
import ReflectionReportModel from '../Models/ReflectionReportModel';

export default class ReflectionsReportStore {
    constructor(globalStore: GlobalStore) {
        this.axios = globalStore.axios;

        makeObservable(this, {
            experiences: observable,
            initialised: observable,
            selectedExperienceIds: observable,
            selectedReflectionIds: observable,
            selectedSkillNames: observable,
            selectedExperiences: computed,
            selectedReflections: computed,
            selectExperience: action,
            selectReflection: action,
            selectSkill: action,
            selectAll: action
        });
    }

    axios: AxiosInstance;

    experiences?: ReflectionReportModel[];

    selectedExperienceIds: number[] = [];

    selectedReflectionIds: number[] = [];

    selectedSkillNames: string[] = [];

    initialised = false;

    get selectedExperiences() {
        return this.experiences?.filter(e => this.selectedExperienceIds.includes(e.id));
    }

    get allSkills() {
        if (this.experiences === undefined) {
            return undefined;
        }

        return uniq(this.experiences.flatMap(e => e.reflections).map(e => e.skill));
    }

    get selectedReflections() {
        return this.experiences?.slice()?.map(e => {
            const copy = createViewModel(e);
            copy.reflections = copy.reflections.filter(r => this.selectedReflectionIds.includes(r.id)) ?? [];
            return copy;
        })?.filter(e => e.reflections.length > 0);
    }

    selectExperience = (id: number) => {
        if (this.selectedExperienceIds.includes(id)) {
            this.selectedExperienceIds = this.selectedExperienceIds.filter(x => x !== id);
        } else {
            this.selectedExperienceIds.push(id);
        }
        const reflections = this.experiences?.find(e => e.id === id)?.reflections;
        reflections?.forEach(reflection => this.selectReflection(reflection.id, this.selectedExperienceIds.includes(id)));
    };

    selectReflection = (id: number, selected?: boolean, parent?: number | string) => {
        if (this.selectedReflectionIds.includes(id) || selected === false) {
            this.selectedReflectionIds = this.selectedReflectionIds.filter(x => x !== id);
        } else {
            this.selectedReflectionIds.push(id);
        }
        if (parent !== undefined) {
            this.selectParent(parent);
        }
    };

    selectSkill = (name: string) => {
        if (this.selectedSkillNames.includes(name)) {
            this.selectedSkillNames = this.selectedSkillNames.filter(x => x !== name);
        } else {
            this.selectedSkillNames.push(name);
        }
        const reflections = this.experiences?.find(e => e.reflections.find(r => r.skill === name))?.reflections;
        const skillReflections = reflections?.filter(x => x.skill === name);
        skillReflections?.forEach(reflection => this.selectReflection(reflection.id, this.selectedSkillNames.includes(name)));
    };

    selectParent = (parent: number | string) => {
        let toggle = false;
        if (typeof parent === 'number') {
            const parentExperience = this.experiences?.find(e => e.id === parent);
            parentExperience?.reflections.forEach(reflection => {
                if (this.selectedReflectionIds.find(r => r === reflection.id)) { toggle = true; }
            });
            if (!toggle) { this.selectExperience(parent); }
        } else if (typeof parent === 'string') {
            const reflections = this.experiences?.find(e => e.reflections.find(r => r.skill === parent))?.reflections;
            const skillReflections = reflections?.filter(x => x.skill === parent);
            skillReflections?.forEach(reflection => {
                if (this.selectedReflectionIds.find(r => r === reflection.id)) { toggle = true; }
            });
            if (!toggle) { this.selectSkill(parent); }
        }
    };

    selectAll = () => {
        if (this.selectedExperienceIds.length !== (this.experiences?.length ?? 0)) {
            this.selectedExperienceIds = this.experiences?.map(e => e.id) ?? [];
            this.selectedReflectionIds = this.experiences?.flatMap(e => e.reflections).map(r => r.id) ?? [];
            this.selectedSkillNames = this.allSkills ?? [];
        } else {
            this.selectedExperienceIds = [];
            this.selectedReflectionIds = [];
            this.selectedSkillNames = [];
        }
    };

    initialiseAsync = async () => {
        if (this.initialised) {
            return;
        }
        runInAction(() => {
            this.initialised = true;
        });
        const experience = await this.axios.get<ReflectionReportModel[]>('/api/reflections');
        runInAction(() => {
            this.experiences = experience.data;
        });
        this.selectAll();
    };
}
